import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  DatePicker,
  Button,
  Space,
  Typography,
  Select,
  Spin,
  message,
  Divider,
  Collapse,
  Tag,
  Alert
} from 'antd';
import {
  DownloadOutlined,
  PrinterOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import apiService from '@/services/apiService';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;

interface GeneralLedgerEntry {
  date: string;
  // accountingDate (vendor bills + manual JEs) and invoiceDate (invoice JEs)
  // are the user-facing posting dates. The displayed Date column resolves
  // invoiceDate || accountingDate || date in that priority.
  accountingDate: string | null;
  invoiceDate: string | null;
  // Analytic dimensions. Partner comes from the journal entry header, department
  // from the individual line — so two lines of one entry can show different
  // departments. Department is only set where someone chose one on the source
  // document, so a blank is real data, not a missing lookup.
  partner: string | null;
  department: string | null;
  journalEntryNo: string;
  journalEntryId: string;
  reference: string | null;
  description: string;
  debit: number;
  credit: number;
  balance: number;
}

// Same resolver used by the flat GL view. Keeps the two GL surfaces in sync.
const resolveDisplayDate = (entry: GeneralLedgerEntry): string =>
  entry.invoiceDate ?? entry.accountingDate ?? entry.date;

interface AccountInfo {
  id: string;
  code: string;
  name: string;
  accountType: string;
}

interface GeneralLedgerAccount {
  account: AccountInfo;
  fromDate: string | null;
  toDate: string | null;
  openingBalance: number;
  closingBalance: number;
  totalDebits: number;
  totalCredits: number;
  entries: GeneralLedgerEntry[];
}

const formatCurrency = (amount: number) => {
  if (amount === 0) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'HKD',
    minimumFractionDigits: 2
  }).format(amount);
};

const getAccountTypeColor = (type: string) => {
  const colors: Record<string, string> = {
    ASSET: 'blue',
    LIABILITY: 'orange',
    EQUITY: 'purple',
    REVENUE: 'green',
    EXPENSE: 'red'
  };
  return colors[type] || 'default';
};

// Opened by clicking a figure on the Profit & Loss or the Balance Sheet, the
// address says which account and which period produced that figure:
//
//   /accounting/general-ledger?accountId=…&fromDate=…&toDate=…
//
// A balance sheet figure is the total of every posting up to its date, so a
// balance sheet sends no fromDate. That is deliberate: with no start date the
// opening balance is nil and every entry that makes up the balance is listed,
// so the closing balance below equals the figure that was clicked.
const readReportLink = () => {
  const q = new URLSearchParams(window.location.search);
  const accountId = q.get('accountId') || undefined;
  const parse = (v: string | null) => (v && dayjs(v).isValid() ? dayjs(v) : null);
  return {
    accountId,
    fromDate: parse(q.get('fromDate')),
    toDate: parse(q.get('toDate')),
    active: !!accountId
  };
};

const GeneralLedgerReport: React.FC = () => {
  const [link] = useState(readReportLink);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<GeneralLedgerAccount[]>([]);
  const [accounts, setAccounts] = useState<AccountInfo[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string | undefined>(link.accountId);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>(
    link.active
      ? [link.fromDate, link.toDate]
      : [dayjs().startOf('year'), dayjs()]
  );
  // Cleared as soon as the user changes a filter — from then on what is on
  // screen is their own query, not the figure they arrived from.
  const [cameFromReport, setCameFromReport] = useState(link.active);

  // Fetch available accounts for filtering
  const fetchAccounts = async () => {
    try {
      const response = await apiService.get('/accounting/accounts');
      const accountList = response.data || response;
      setAccounts(accountList);
    } catch (error: any) {
      console.error('Failed to fetch accounts:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      if (selectedAccountId) {
        params.append('accountId', selectedAccountId);
      }
      if (dateRange[0]) {
        params.append('fromDate', dateRange[0].format('YYYY-MM-DD'));
      }
      if (dateRange[1]) {
        params.append('toDate', dateRange[1].format('YYYY-MM-DD'));
      }

      const response = await apiService.get(`/accounting/reports/general-ledger?${params}`);
      setData(response.data || response);
    } catch (error: any) {
      message.error(error.message || 'Failed to load general ledger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  // Refetch whenever the account or date-range filter changes. Without this the
  // report (and therefore Export, which serialises whatever is in `data`) kept
  // serving the range fetched on mount until the user happened to press Refresh.
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId, dateRange[0]?.valueOf(), dateRange[1]?.valueOf()]);

  const columns: ColumnsType<GeneralLedgerEntry> = [
    {
      title: 'Date',
      key: 'date',
      width: 120,
      render: (_: any, record: GeneralLedgerEntry) => dayjs(resolveDisplayDate(record)).format('YYYY-MM-DD')
    },
    {
      title: 'Journal Entry',
      dataIndex: 'journalEntryNo',
      key: 'journalEntryNo',
      width: 130
    },
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      width: 120,
      render: (val: string | null) => val || '-'
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 250,
      ellipsis: true
    },
    {
      title: 'Partner',
      dataIndex: 'partner',
      key: 'partner',
      width: 180,
      ellipsis: true,
      render: (v: string | null) => v || '-'
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      width: 130,
      ellipsis: true,
      render: (v: string | null) => v || '-'
    },
    {
      title: 'Debit',
      dataIndex: 'debit',
      key: 'debit',
      width: 120,
      align: 'right',
      render: (val: number) => formatCurrency(val)
    },
    {
      title: 'Credit',
      dataIndex: 'credit',
      key: 'credit',
      width: 120,
      align: 'right',
      render: (val: number) => formatCurrency(val)
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      key: 'balance',
      width: 130,
      align: 'right',
      render: (val: number) => (
        <Text strong style={{ color: val >= 0 ? '#1890ff' : '#f5222d' }}>
          {formatCurrency(val)}
        </Text>
      )
    }
  ];

  // Once the user picks their own filters the address no longer describes what
  // is on screen, so drop it. Otherwise a refresh would silently snap back to
  // the figure they arrived from.
  const leaveReportLink = () => {
    setCameFromReport(false);
    if (window.location.search) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  };

  const handleDateChange = (dates: any) => {
    setDateRange(dates || [null, null]);
    leaveReportLink();
  };

  const handleAccountChange = (accountId: string | undefined) => {
    setSelectedAccountId(accountId);
    leaveReportLink();
  };

  // Back to the default view: every account, this year to date.
  const handleShowEverything = () => {
    leaveReportLink();
    setSelectedAccountId(undefined);
    setDateRange([dayjs().startOf('year'), dayjs()]);
  };

  const handleExport = () => {
    if (data.length === 0) {
      message.warning('No data to export');
      return;
    }

    const rows: Record<string, string | number>[] = [];
    let totalEntries = 0;

    data.forEach(ledger => {
      // Account header row
      rows.push({
        'Account Code': ledger.account.code,
        'Account Name': ledger.account.name,
        'Account Type': ledger.account.accountType,
        'Date': '',
        'Journal Entry': '',
        'Reference': '',
        'Description': `Opening Balance: ${ledger.openingBalance.toFixed(2)}`,
        'Partner': '',
        'Department': '',
        'Debit': '',
        'Credit': '',
        'Balance': ledger.openingBalance,
      });

      // Entry rows
      ledger.entries.forEach(entry => {
        rows.push({
          'Account Code': ledger.account.code,
          'Account Name': ledger.account.name,
          'Account Type': ledger.account.accountType,
          'Date': dayjs(resolveDisplayDate(entry)).format('YYYY-MM-DD'),
          'Journal Entry': entry.journalEntryNo,
          'Reference': entry.reference || '',
          'Description': entry.description,
          'Partner': entry.partner || '',
          'Department': entry.department || '',
          'Debit': entry.debit,
          'Credit': entry.credit,
          'Balance': entry.balance,
        });
        totalEntries++;
      });

      // Account totals row
      rows.push({
        'Account Code': ledger.account.code,
        'Account Name': ledger.account.name,
        'Account Type': '',
        'Date': '',
        'Journal Entry': '',
        'Reference': '',
        'Description': 'TOTALS',
        'Partner': '',
        'Department': '',
        'Debit': ledger.totalDebits,
        'Credit': ledger.totalCredits,
        'Balance': ledger.closingBalance,
      });

      // Empty separator row
      rows.push({ 'Account Code': '', 'Account Name': '', 'Account Type': '', 'Date': '', 'Journal Entry': '', 'Reference': '', 'Description': '', 'Partner': '', 'Department': '', 'Debit': '', 'Credit': '', 'Balance': '' });
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 12 }, { wch: 30 }, { wch: 12 }, { wch: 12 },
      { wch: 14 }, { wch: 15 }, { wch: 30 },
      { wch: 28 }, { wch: 16 },
      { wch: 14 }, { wch: 14 }, { wch: 14 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'General Ledger');

    const period = dateRange[0] && dateRange[1]
      ? `${dateRange[0].format('YYYYMMDD')}-${dateRange[1].format('YYYYMMDD')}`
      : dayjs().format('YYYYMMDD');
    XLSX.writeFile(wb, `General_Ledger_${period}.xlsx`);
    message.success(`Exported ${data.length} account(s), ${totalEntries} entries`);
  };

  const handlePrint = () => {
    window.print();
  };

  const renderAccountLedger = (ledger: GeneralLedgerAccount) => {
    const headerLabel = (
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Space>
          <Text strong>{ledger.account.code}</Text>
          <Text>{ledger.account.name}</Text>
          <Tag color={getAccountTypeColor(ledger.account.accountType)}>
            {ledger.account.accountType}
          </Tag>
        </Space>
        <Space size="large">
          <Text type="secondary">
            Opening: <Text strong>{formatCurrency(ledger.openingBalance)}</Text>
          </Text>
          <Text type="secondary">
            Closing: <Text strong style={{ color: '#1890ff' }}>{formatCurrency(ledger.closingBalance)}</Text>
          </Text>
        </Space>
      </Space>
    );

    return (
      <Panel header={headerLabel} key={ledger.account.id}>
        <Table
          columns={columns}
          dataSource={ledger.entries}
          rowKey={(record, index) => `${record.journalEntryId}-${index}`}
          pagination={{ defaultPageSize: 20, showSizeChanger: true }}
          bordered
          size="small"
          scroll={{ x: 1000 }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                <Table.Summary.Cell index={0} colSpan={4}>
                  <Text strong>Totals</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">
                  <Text strong>{formatCurrency(ledger.totalDebits)}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  <Text strong>{formatCurrency(ledger.totalCredits)}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} align="right">
                  <Text strong style={{ color: '#1890ff' }}>
                    {formatCurrency(ledger.closingBalance)}
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </Panel>
    );
  };

  return (
    <Card
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>General Ledger</Title>
          <Tag>{data.length} Account(s)</Tag>
        </Space>
      }
      extra={
        <Space>
          <Select
            placeholder="All Accounts"
            allowClear
            style={{ width: 250 }}
            value={selectedAccountId}
            onChange={handleAccountChange}
            showSearch
            // Search the label, not the children. Written as two expressions
            // either side of a dash, the children are the array
            // ['124000', ' - ', 'Account Receivable'] rather than a string, and
            // the old filter called toLowerCase() straight on it — which threw
            // on every keystroke and took the whole page down with it.
            optionFilterProp="label"
          >
            {accounts.map(acc => (
              <Select.Option key={acc.id} value={acc.id} label={`${acc.code} - ${acc.name}`}>
                {acc.code} - {acc.name}
              </Select.Option>
            ))}
          </Select>
          <RangePicker
            value={dateRange as [Dayjs, Dayjs]}
            onChange={handleDateChange}
            format="YYYY-MM-DD"
            allowClear
          />
          <Button icon={<ReloadOutlined />} onClick={fetchData}>
            Refresh
          </Button>
          <Button icon={<DownloadOutlined />} onClick={handleExport}>
            Export
          </Button>
          <Button icon={<PrinterOutlined />} onClick={handlePrint}>
            Print
          </Button>
        </Space>
      }
    >
      <Spin spinning={loading}>
        {/* Arrived from a figure on a report: say so, so a filtered ledger is
            never mistaken for the whole ledger. */}
        {cameFromReport && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              <>
                Showing the transactions behind{' '}
                <strong>
                  {(() => {
                    const acc = accounts.find(a => a.id === selectedAccountId);
                    return acc ? `${acc.code} - ${acc.name}` : 'the account you clicked';
                  })()}
                </strong>
                {dateRange[0]
                  ? <> for <strong>{dateRange[0].format('YYYY-MM-DD')}</strong> to <strong>{dateRange[1]?.format('YYYY-MM-DD')}</strong>. Closing less opening is the figure on the report.</>
                  : <> up to <strong>{dateRange[1]?.format('YYYY-MM-DD')}</strong>. The closing balance is the figure on the report.</>}
              </>
            }
            action={
              <Button size="small" onClick={handleShowEverything}>
                Show all accounts
              </Button>
            }
          />
        )}

        {data.length > 0 ? (
          <Collapse defaultActiveKey={data.length === 1 ? [data[0].account.id] : []}>
            {data.map(ledger => renderAccountLedger(ledger))}
          </Collapse>
        ) : (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Text type="secondary">No ledger entries found for the selected criteria</Text>
          </div>
        )}

        {dateRange[0] && dateRange[1] && (
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Text type="secondary">
              Period: {dateRange[0].format('MMM DD, YYYY')} to {dateRange[1].format('MMM DD, YYYY')}
            </Text>
          </div>
        )}
      </Spin>
    </Card>
  );
};

export default GeneralLedgerReport;